import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Markup de la referencia. Lo único que se añade es el cableado real: la
// referencia solo hace setState, aquí el alta va contra /api/subscribe.php,
// que es el endpoint que alimenta el panel de suscriptores.
export default function NewsletterSection() {
  const sectionRef = useRef(null);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const els = section.querySelectorAll('.news-reveal');
      els.forEach((el, i) => {
        gsap.from(el, {
          y: 40, opacity: 0, duration: 0.8, ease: 'expo.out', delay: i * 0.12,
          scrollTrigger: { trigger: section, start: 'top 80%' },
        });
      });
    }, section);

    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setErrorMsg('Ingresa un correo válido.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch('/api/subscribe.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
      } else {
        setErrorMsg(data.error || 'No fue posible suscribirte. Intenta de nuevo.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Error de conexión. Intenta de nuevo.');
      setStatus('error');
    }
  };

  return (
    <section
      ref={sectionRef}
      id="contacto"
      className="relative w-full py-28 md:py-36"
      style={{ background: 'linear-gradient(180deg, #e8e2d6 0%, #f0ebe4 100%)' }}
    >
      <div className="max-w-[600px] mx-auto px-6 text-center">
        <span className="news-reveal block font-mono text-[11px] uppercase tracking-[0.2em] text-[#C9A26E] mb-6">
          Para familias empresarias
        </span>

        <h2 className="news-reveal font-heading text-[#1a1a1a] text-[clamp(24px,3vw,36px)] leading-[1.2] tracking-[-0.01em] mb-4">
          El conocimiento que protege el legado.
        </h2>

        <p className="news-reveal font-body text-[#6b6560] text-base leading-[1.7] mb-3">
          Una reflexión semanal sobre gobierno corporativo, continuidad familiar y trascendencia empresarial. Directamente en tu correo.
        </p>

        <p className="news-reveal font-body text-[#9a9488] text-sm italic mb-10">
          Sin spam. Sin ruido. Solo lo que importa.
        </p>

        {status === 'success' ? (
          <div className="news-reveal py-6">
            <div className="w-10 h-10 rounded-full bg-[#C9A26E]/10 flex items-center justify-center mx-auto mb-4">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#C9A26E]">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="font-body text-[#1a1a1a] text-lg">Gracias por suscribirte.</p>
            <p className="font-body text-[#6b6560] text-sm mt-1">Pronto recibirás nuestra primera reflexión.</p>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} noValidate className="news-reveal flex flex-col sm:flex-row gap-3 justify-center">
              <label htmlFor="newsletter-email" className="sr-only">Correo electrónico</label>
              <input
                id="newsletter-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                aria-describedby={status === 'error' ? 'newsletter-error' : undefined}
                className="w-full sm:w-[300px] h-14 px-5 border border-[#1a1a1a]/10 bg-white/50 backdrop-blur-sm rounded-sm font-body text-sm text-[#1a1a1a] placeholder:text-[#9a9488] focus:outline-none focus:border-[#C9A26E]/60 transition-colors duration-300"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="h-14 px-8 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-[#f0ebe2] font-body text-[12px] font-medium uppercase tracking-[0.1em] rounded-sm transition-colors duration-400 active:scale-[0.97] whitespace-nowrap disabled:opacity-70"
              >
                {status === 'loading' ? 'Enviando…' : 'Suscribirse'}
              </button>
            </form>
            {status === 'error' && (
              <p id="newsletter-error" role="alert" className="font-body text-sm text-[#C62828] mt-4">
                {errorMsg}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
